import React, { useState, useEffect } from "react";
import "./styles.scss";

// Length of the semicircle arc (π * radius)
const ARC_LENGTH = Math.PI * 50;

// Text shown instead of a value, a failed or incomplete load is a warning and not "nothing to show"
const NO_DATA = {
  loading: { text: "Loading…", tooltip: "The error rate is loading." },
  empty: { text: "No data", tooltip: "No data in the selected period." },
  incomplete: {
    text: "Incomplete data",
    tooltip:
      "Errors were found, but no tag calls or dataLayer events to compare them with, so the error rate can't be calculated.",
  },
  failed: { text: "Could not be loaded", tooltip: "The error rate could not be loaded." },
};

/**
 * ErrorRateGauge Component - Compact semi-circular gauge for an error percentage
 *
 * @param {number|null} value - Error percentage (0-100), null when there is no data
 * @param {string} status - "ok", or why there is no value: "loading", "empty", "incomplete" or "failed" (see getErrorRate)
 * @param {string} label - Label shown below the gauge
 */
const ErrorRateGauge = ({ value, status = "ok", label }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const hasData = status === "ok" && Number.isFinite(value);
  const noData = NO_DATA[status] || NO_DATA.empty;
  const percentage = hasData ? Math.min(Math.max(value, 0), 100) : 0;

  // Animate the value change
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValue(percentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  // Interpolate between green (low errors) and #F62064 (high errors)
  const getArcColor = (ratio) => {
    const startColor = { r: 122, g: 199, b: 26 };
    const endColor = { r: 246, g: 32, b: 100 }; // #F62064

    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div className="error-rate-gauge">
      <div className="error-rate-gauge-arc">
        <svg viewBox="0 0 120 66" className="error-rate-gauge-svg">
          {/* Background Arc */}
          <path
            d="M 10 60 A 50 50 0 0 1 110 60"
            fill="none"
            stroke="#ececf0"
            strokeWidth="8"
            strokeLinecap="round"
          />

          {/* Progress Arc */}
          <path
            d="M 10 60 A 50 50 0 0 1 110 60"
            fill="none"
            stroke={getArcColor(animatedValue / 100)}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={ARC_LENGTH}
            strokeDashoffset={ARC_LENGTH - (ARC_LENGTH * animatedValue) / 100}
            opacity={animatedValue > 0 ? 1 : 0}
            className="error-rate-gauge-progress"
          />
        </svg>

        {/* Below 0.05% the value would round to 0.0% while there are errors */}
        {hasData ? (
          <div className="error-rate-gauge-value">
            {percentage > 0 && percentage < 0.05 ? "< 0.1%" : `${percentage.toFixed(1)}%`}
          </div>
        ) : (
          <div
            className={`error-rate-gauge-value error-rate-gauge-value-no-data error-rate-gauge-value-${status}`}
            title={noData.tooltip}
          >
            {noData.text}
          </div>
        )}
      </div>

      {label && <div className="error-rate-gauge-label">{label}</div>}
    </div>
  );
};

export default ErrorRateGauge;
